/**
 * Rentals Board Configuration
 *
 * Configuration for the rentals board instance with all features enabled.
 * Supports dynamic categories loaded from Firestore per-location.
 */

import { getRentalCollectionPath, getRentalRTDBPath } from '@/shared/config';
import type { BoardConfig } from '../types';
import type { RentalCategory } from '@/types/rental.types';
import { DEFAULT_RENTAL_CATEGORIES } from '@/types/rental.types';


/**
 * Build a rentals board configuration from dynamic categories
 *
 * @param categories - Array of category definitions (fetched from Firestore)
 * @returns BoardConfig configured for the given categories
 *
 * @example
 * const { categories } = useRentalCategories(locationId);
 * const config = useMemo(() => buildRentalsConfig(categories), [categories]);
 */
export function buildRentalsConfig(categories: RentalCategory[]): BoardConfig<string> {
  // Sort categories by sortOrder to ensure correct tab order
  const sortedCategories = [...categories].sort((a, b) => a.sortOrder - b.sortOrder);

  // Build categoryTypes mapping (only include non-standard types)
  const categoryTypesEntries = sortedCategories
    .filter((c) => c.categoryType && c.categoryType !== 'standard')
    .map((c) => [c.id, c.categoryType!] as const);
  const categoryTypes = categoryTypesEntries.length > 0
    ? (Object.fromEntries(categoryTypesEntries) as Partial<Record<string, 'standard' | 'multiples'>>)
    : undefined;

  return {
    boardType: 'rentals',

    // Extract category IDs as the categories array
    categories: sortedCategories.map((c) => c.id),

    // Build display names mapping from category definitions
    categoryDisplayNames: Object.fromEntries(
      sortedCategories.map((c) => [c.id, c.displayName])
    ) as Record<string, string>,

    // Map category IDs to their type (multiples vs standard)
    categoryTypes,

    features: {
      linkedItems: true,
      actionItems: true,
      fuelCalculator: true,
      itemFlags: true,
    },

    getCollectionPath: getRentalCollectionPath,
    getRTDBPath: getRentalRTDBPath,
  };
}

/**
 * Default rentals board configuration
 * Uses DEFAULT_RENTAL_CATEGORIES for backward compatibility.
 * Components should prefer buildRentalsConfig with dynamic categories.
 */
export const RENTALS_CONFIG: BoardConfig<string> = buildRentalsConfig(DEFAULT_RENTAL_CATEGORIES);

// Re-export shared config and status colors
export {
  statusColors,
} from './shared.config';
